/**
 * AppSimulator.jsx — admin-only mobile PhoneFrame preview at /app/preview.
 *
 * After the Feb 2026 refactor, every screen lives in its own file under
 * `pages/app/screens/*.jsx` and is re-exported through the barrel at
 * `@/pages/app/screens`. This file is now just:
 *   • Chrome (TopBar, ShortcutBtn, PhoneNav, BottomTabs)
 *   • The `AppSimulator` default export — the mobile phone-frame preview that
 *     admins can use at `/app/preview` to see how the mobile app will feel.
 * Consumers of individual screens should import from `@/pages/app/screens`.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Brain, Bell, Settings as SettingsIcon, LogOut,
  Sparkles, ChevronRight, Battery, Wifi, Home, PlugZap, Mail, Zap,
  CalendarDays, NotebookPen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PhoneFrame from "@/components/PhoneFrame";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

import {
  HomeScreen, ChatScreen, MemoriesScreen, RemindersScreen, SettingsScreen,
  PersonalityScreen, VoiceScreen, LanguageScreen, PrivacyScreen, PlansScreen,
  FamilyScreen, FindScreen, OtaScreen, PairScreen, CalendarScreen,
  IntegrationsScreen, InboxScreen, EmailsScreen, FocusScreen, SentRepliesScreen,
} from "@/pages/app/screens";

export default function AppSimulator() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [screen, setScreen] = useState("home");
  const [device, setDevice] = useState(null);
  const [personalities, setPersonalities] = useState([]);
  const [voices, setVoices] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [chatExpression, setChatExpression] = useState("idle");
  const [pendingConvId, setPendingConvId] = useState(null);

  useEffect(() => {
    api.get("/device").then(r => setDevice(r.data)).catch(() => {});
    api.get("/catalog/personalities").then(r => setPersonalities(r.data)).catch(() => {});
    api.get("/catalog/voices").then(r => setVoices(r.data)).catch(() => {});
    api.get("/catalog/languages").then(r => setLanguages(r.data)).catch(() => {});
    api.get("/catalog/plans").then(r => setPlans(r.data)).catch(() => {});
  }, []);

  // Handle OAuth callback returns (Google Calendar).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") !== "calendar") return;
    const status = params.get("status");
    const detail = params.get("detail");
    if (status === "ok") {
      toast.success("Google Calendar connected — Rammy can now read your events.");
      refresh?.();
      setScreen("calendar");
    } else {
      const messages = {
        invalid_state: "That sign-in link expired. Try connecting again.",
        state_expired: "That sign-in link expired. Try connecting again.",
        token_exchange_failed: "Google couldn't complete the handshake. Try again.",
        no_refresh_token: "Google didn't share offline access. Visit myaccount.google.com/permissions, remove Rammy, then retry.",
        missing_code_or_state: "Google returned an incomplete response.",
      };
      toast.error(messages[detail] || `Couldn't connect Google Calendar${detail ? ` — ${detail}` : ""}`);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const go = (s) => setScreen(s);

  return (
    <div className="min-h-screen grain" style={{ background: "var(--rammy-bg)" }}>
      <TopBar user={user} onLogout={async () => { await logout(); nav("/"); }} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16 grid lg:grid-cols-12 gap-10">
        {/* LEFT: context */}
        <aside className="lg:col-span-4 fade-up">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold bg-[var(--rammy-cyan)]/10 border border-[var(--rammy-cyan)]/30 rounded-full px-3 py-1" data-testid="preview-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan-dark)] animate-pulse" />
            Admin · Mobile app preview
          </div>
          <h1 className="mt-3 text-4xl font-display leading-tight">The Rammy app,<br />on your desktop.</h1>
          <p className="mt-4 text-[var(--rammy-text-sec)] leading-relaxed">
            Live preview of the iOS/Android app inside a phone frame — for design review and manufacturing feedback.
            Regular users get the full-page desktop workspace at <Link to="/app" className="underline underline-offset-2 hover:text-[var(--rammy-cyan-dark)]" data-testid="preview-open-app">/app</Link>.
          </p>

          <div className="mt-8 space-y-3">
            <ShortcutBtn testid="short-chat"      active={screen === "chat"}      onClick={() => go("chat")}      icon={<MessageCircle size={16} />} label="Talk to Rammy" />
            <ShortcutBtn testid="short-memories"  active={screen === "memories"}  onClick={() => go("memories")}  icon={<Brain size={16} />}         label="Rings" />
            <ShortcutBtn testid="short-reminders" active={screen === "reminders"} onClick={() => go("reminders")} icon={<Bell size={16} />}          label="Reminders" />
            <ShortcutBtn testid="short-settings"  active={screen === "settings"}  onClick={() => go("settings")}  icon={<SettingsIcon size={16} />}  label="Device settings" />
            <ShortcutBtn testid="short-plans"     active={screen === "plans"}     onClick={() => go("plans")}     icon={<Sparkles size={16} />}      label="Subscription" />
          </div>

          <div className="mt-10 rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 rammy-shadow">
            <div className="text-sm font-medium">Your Rammy</div>
            {device && (
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-[var(--rammy-text-muted)]">Status</div><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Online</div>
                <div className="text-[var(--rammy-text-muted)]">Battery</div><div>{device.battery}%</div>
                <div className="text-[var(--rammy-text-muted)]">WiFi</div><div>{device.wifi}</div>
                <div className="text-[var(--rammy-text-muted)]">Firmware</div><div className="font-mono text-xs">{device.firmware}</div>
              </div>
            )}
            <Link to="/device"><Button className="btn-pill btn-dark w-full mt-5" data-testid="open-device">Open robot face fullscreen</Button></Link>
          </div>
        </aside>

        {/* RIGHT: phone frame */}
        <div className="lg:col-span-8 flex justify-center fade-up">
          <PhoneFrame>
            <PhoneNav screen={screen} setScreen={setScreen} />
            <div className="h-[calc(720px-3.75rem-4.25rem)] overflow-y-auto subtle-scroll pb-2">
              {screen === "home"         && <HomeScreen user={user} device={device} go={go} resumeConversation={(id) => { setPendingConvId(id); go("chat"); }} setChatExpression={setChatExpression} />}
              {screen === "chat"         && <ChatScreen user={user} expression={chatExpression} setExpression={setChatExpression} initialConversationId={pendingConvId} onConsumeInitial={() => setPendingConvId(null)} />}
              {screen === "memories"     && <MemoriesScreen />}
              {screen === "reminders"    && <RemindersScreen />}
              {screen === "settings"     && <SettingsScreen device={device} setDevice={setDevice} go={go} user={user} refresh={refresh} />}
              {screen === "personality"  && <PersonalityScreen personalities={personalities} user={user} refresh={refresh} go={go} />}
              {screen === "voice"        && <VoiceScreen voices={voices} user={user} refresh={refresh} go={go} />}
              {screen === "language"     && <LanguageScreen languages={languages} user={user} refresh={refresh} go={go} />}
              {screen === "privacy"      && <PrivacyScreen device={device} setDevice={setDevice} go={go} />}
              {screen === "plans"        && <PlansScreen plans={plans} user={user} go={go} />}
              {screen === "family"       && <FamilyScreen go={go} />}
              {screen === "find"         && <FindScreen device={device} go={go} />}
              {screen === "ota"          && <OtaScreen device={device} go={go} />}
              {screen === "pair"         && <PairScreen go={go} />}
              {screen === "calendar"     && <CalendarScreen user={user} refresh={refresh} go={go} />}
              {screen === "integrations" && <IntegrationsScreen user={user} refresh={refresh} go={go} />}
              {screen === "inbox"        && <InboxScreen user={user} go={go} />}
              {screen === "emails"       && <EmailsScreen user={user} go={go} />}
              {screen === "focus"        && <FocusScreen user={user} go={go} />}
              {screen === "sent-replies" && <SentRepliesScreen user={user} go={go} />}
            </div>
            <BottomTabs screen={screen} go={go} />
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Chrome — TopBar, sidebar shortcut buttons, phone in-frame nav       */
/* ------------------------------------------------------------------ */

function TopBar({ user, onLogout }) {
  const isAdmin = user?.role === "admin";
  return (
    <nav className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[var(--rm-surface)]/70 border-b border-[var(--rammy-border)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="app-logo">
          <div className="w-8 h-8 rounded-xl bg-[var(--rm-ink-900)] flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-[var(--rammy-cyan)] shadow-[0_0_10px_var(--rammy-cyan)]" />
          </div>
          <span className="font-display text-lg">Rammy</span>
        </Link>
        <div className="flex items-center gap-6 text-sm">
          <Link to="/device" className="hover:opacity-70" data-testid="app-open-face">Robot face</Link>
          {isAdmin && <Link to="/admin" className="hover:opacity-70" data-testid="app-open-admin">Dashboard</Link>}
          <div className="flex items-center gap-2 text-[var(--rammy-text-sec)]">
            <span className="text-xs uppercase tracking-widest">{user?.plan}</span>
            <span className="w-6 h-6 rounded-full bg-[var(--rammy-cyan)]/30 text-xs flex items-center justify-center font-medium">
              {(user?.name || user?.email || "N")[0]?.toUpperCase()}
            </span>
          </div>
          <button onClick={onLogout} className="text-[var(--rammy-text-muted)] hover:text-red-500" data-testid="app-logout">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </nav>
  );
}

function ShortcutBtn({ icon, label, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center justify-between px-5 py-3 rounded-2xl border transition-all ${
        active ? "bg-[var(--rm-ink-900)] text-white border-transparent" : "bg-[var(--rm-surface)] border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
      }`}
    >
      <span className="flex items-center gap-3">{icon}<span className="font-medium">{label}</span></span>
      <ChevronRight size={16} />
    </button>
  );
}

function PhoneNav({ screen, setScreen }) {
  const back = ["personality", "voice", "language", "privacy", "plans", "family", "find", "ota", "pair", "calendar", "integrations", "inbox", "emails", "focus", "sent-replies"].includes(screen);
  return (
    <div className="h-14 flex items-center justify-between px-4 border-b border-[var(--rammy-border)]">
      {back ? (
        <button onClick={() => setScreen("home")} data-testid="phone-back" className="text-xs uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">
          ← Back
        </button>
      ) : <span className="text-xs uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Rammy</span>}
      <div className="text-xs text-[var(--rammy-text-muted)] font-mono">iPhone preview</div>
    </div>
  );
}

function BottomTabs({ screen, go }) {
  const tabs = [
    { id: "home",         icon: <Home size={18} />,          label: "Home" },
    { id: "chat",         icon: <MessageCircle size={18} />, label: "Chat" },
    { id: "memories",     icon: <Brain size={18} />,         label: "Rings" },
    { id: "reminders",    icon: <Bell size={18} />,          label: "Reminders" },
    { id: "settings",     icon: <SettingsIcon size={18} />,  label: "Settings" },
  ];
  return (
    <div className="h-16 border-t border-[var(--rammy-border)] flex items-center justify-around px-2 bg-[var(--rm-surface)]">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => go(t.id)}
          data-testid={`tab-${t.id}`}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition ${screen === t.id ? "text-[var(--rammy-cyan-dark)]" : "text-[var(--rammy-text-muted)]"}`}
        >
          {t.icon}
          <span className="text-[10px]">{t.label}</span>
        </button>
      ))}
    </div>
  );
}
